/* eslint-disable max-lines-per-function */
import {
   StatisticsData,
   StatisticsDataHeader,
   StatisticsDataRow,
   StatisticsOrder,
} from '@dreryk/egabinet-statistics-frontend-api';

import {
   dateFormatter,
   dateTimeFormatter,
   Formatter,
   integerFormatter,
   priceFormatter,
   stringFormatter,
} from '@dreryk/react-common';

const NO_VALUE = '-';

const getCellValue = (formatter: Formatter<any>, value: string): string => {
   if (value === NO_VALUE) {
      return NO_VALUE;
   }

   return formatter(value) ?? NO_VALUE;
};

const generateCells = (
   headers: StatisticsDataHeader[],
   row: StatisticsDataRow,
   tBodyRow: HTMLTableRowElement
): void => {
   row.cells.forEach((value, index) => {
      const cell = tBodyRow.insertCell();
      switch (headers[index].type) {
         case 'DATE':
            cell.innerHTML = `<div>${getCellValue(dateFormatter, value)}</div>`;
            break;
         case 'NUMBER':
            cell.innerHTML = `<div>${getCellValue(integerFormatter, value)}</div>`;
            cell.style.textAlign = 'end';
            cell.style.width = '4em';
            break;
         case 'TEXT':
            cell.innerHTML = `<div>${getCellValue(stringFormatter, value)}</div>`;
            break;
         case 'MONEY':
            cell.innerHTML = `<div>${getCellValue(priceFormatter, value)}</div>`;
            cell.style.textAlign = 'end';
            cell.style.width = '14em';
            break;
         default:
            cell.innerHTML = `<div>${stringFormatter(value)}</div>`;
            break;
      }

      if (headers[index].width.includes('px')) {
         cell.style.whiteSpace = 'nowrap';
      }
   });
};

const generateTable = (statisticsData: StatisticsData): HTMLTableElement => {
   const { rows, headers } = statisticsData;
   const table = document.createElement('table');
   const tHead = table.createTHead();
   const tBody = table.createTBody();
   const tHeadRow = tHead.insertRow();

   headers.forEach((header) => {
      const cellElement = tHeadRow.insertCell();
      cellElement.innerHTML = `<div>${header.name}</div>`;
      switch (header.type) {
         case 'NUMBER':
            cellElement.style.textAlign = 'end';
            break;
         case 'MONEY':
            cellElement.style.textAlign = 'end';
            break;
         default:
            break;
      }
   });

   rows.forEach((row) => {
      const tBodyRow = tBody.insertRow();
      generateCells(headers, row, tBodyRow);
   });

   return table;
};

/* eslint-disable prettier/prettier */
const getPrintDocument = (statisticsHeaderData: StatisticsOrder, statisticsData: StatisticsData): string => `
<!DOCTYPE html>
<html lang="pl">
<head>
   <meta charset="UTF-8">
   <meta http-equiv="X-UA-Compatible" content="IE=edge">
   <meta name="viewport" content="width= device-width, initial-scale=1.0">
   <title>${statisticsHeaderData.name}</title>
   <style>
      * {
         -webkit-print-color-adjust: exact !important;   /* Chrome, Safari 6 - 15.3, Edge */
         color-adjust: exact !important;                 /* Firefox 48 - 96 */
         print-color-adjust: exact !important;           /* Firefox 97+, Safari 15.4+ */
      }
      html {
         font-size: 10px;
      }
      body {
         font-size: 0.875em;
         line-height: 1.125em;
         margin: 0;
         font-family: 'Rubik',sans-serif;
         font-weight: 400;
         color: #000;
         background-color: #FFF;
      }
      h1 {
         font-size: 1.5em;
         font-weight: 400;
         line-height: 1.75em;
         letter-spacing: 0;
         text-align: left;
         margin: 0.75em 0;
      }
      table {
         width: 100%;
         border-collapse: collapse;
      }
      thead > tr {
         min-height: 2em;
         font-weight: 700;
      }    
      thead > tr > td {
         padding: 0;
         page-break-inside: avoid;
         background: #D3DFE9;
      }      
      thead > tr > td > div {
         padding: 0.4375em 0.5em 0.375em 0.5em;
      }      
      thead > tr > td:first-child {
         border-top-left-radius: 0.75em;
      }
      thead > tr > td:last-child {
         border-top-right-radius: 0.75em;
      }
      tbody > tr {
         min-height: 2em;
         border-bottom: 1px solid #CCCCCC;
      }
      tbody > tr > td {
         padding: 0;
         page-break-inside: avoid;
      }
      tbody > tr > td > div {
         padding: 0.4375em 0.5em 0.375em 0.5em;
      }
      thead {
         page-break-after: avoid;
      }
      .description {
         display: flex;
         gap: 1em;
         margin-bottom: 1.25em;
      }
   </style>
</head>

<body>
   <h1>${statisticsHeaderData.name}</h1>
   <div class='description'>
      <div>Data wygenerowania: ${dateTimeFormatter(statisticsHeaderData.created)}</div>
      <div>Zakres: ${dateFormatter(statisticsHeaderData.range.lowerEndpoint())} - ${dateFormatter(statisticsHeaderData.range.upperEndpoint())}</div>
   </div>
   ${generateTable(statisticsData).outerHTML}
</body>
</html>
`;
/* eslint-enable prettier/prettier */

const createPrintFrame = (srcDoc: string): HTMLIFrameElement => {
   const printFrame = document.createElement('iframe');
   printFrame.id = 'printFrame';
   printFrame.hidden = true;
   printFrame.srcdoc = srcDoc;
   return printFrame;
};

export const printStatistics = (statisticsHeaderData: StatisticsOrder, statisticsData: StatisticsData): void => {
   const printFrame = createPrintFrame(getPrintDocument(statisticsHeaderData, statisticsData));

   document.body.appendChild(printFrame);

   printFrame.onload = () => {
      printFrame.contentWindow!.focus();
      printFrame.contentWindow!.print();
      printFrame.contentWindow!.addEventListener('afterprint', () => printFrame.remove());
   };
};
